import type { ScenePublicApi } from "@/layers/Compositor";
import Logger from "@mm/logging";
import { type GuiTimeZone, ProjectionNameEnum, type WmsLayer } from "@mm/metx-workbench.meteomatics.com";
import { type BinaryAnomalyDefinition, getBinaryAnomalyDefinitionByPrimaryParameter } from "./BinaryAnomalyDefinitions";
import { createBinaryAnomalyColorMapping } from "./BinaryAnomalyStyle";
import { createBinaryAnomalyTileGetter } from "./BinaryAnomalyTileGetter";
import { BinaryColormapLayerImpl } from "./BinaryColormapLayerImpl";

const logger = Logger.fromFilename(__filename);

export type BinaryAnomalyRuntimeStatus =
  | "loading"
  | "ready"
  | "unsupported_source"
  | "source_error"
  | "style_error"
  | "unsupported_hardware";

type MutableBinaryAnomalyRuntimeState = {
  status: BinaryAnomalyRuntimeStatus;
  error?: unknown;
};

function setRuntimeStatus(
  state: MutableBinaryAnomalyRuntimeState,
  status: BinaryAnomalyRuntimeStatus,
  context: { model: string; stage?: string },
  error?: unknown,
): void {
  if (state.status !== status) {
    const message = `Anomaly status changed from ${state.status} to ${status} for model ${context.model}${
      context.stage ? ` at ${context.stage}` : ""
    }.`;
    if (status === "source_error" || status === "style_error" || status === "unsupported_hardware") {
      logger.warn(message);
    } else {
      logger.log(message);
    }
  }
  state.status = status;
  state.error = error;
}

function isUnsupportedSourceError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("kind" in error)) {
    return false;
  }
  return error.kind === "UnknownParameterError" || error.kind === "MissingDataError";
}

function fixedAnomalyProps(props: WmsLayer, definition: BinaryAnomalyDefinition): WmsLayer {
  return {
    ...props,
    parameter_unit: definition.primaryParameter,
    ens_select: "",
    calibrated: false,
    vertical_interpolation: "none",
    color_map: props.color_map || definition.style.id,
    custom_options: {
      api_url_suffix: props.custom_options?.api_url_suffix,
    },
  };
}

export class BinaryAnomalyLayerImpl extends BinaryColormapLayerImpl {
  private readonly runtimeState: MutableBinaryAnomalyRuntimeState;
  private readonly definition: BinaryAnomalyDefinition;

  constructor(
    uid: number,
    props: WmsLayer,
    scene: ScenePublicApi,
    timezone: GuiTimeZone,
    runtimeState: MutableBinaryAnomalyRuntimeState = { status: "loading" },
  ) {
    const definition = getBinaryAnomalyDefinitionByPrimaryParameter(props.parameter_unit);
    if (!definition) {
      throw new Error(`unsupported binary anomaly parameter ${props.parameter_unit}`);
    }
    super(uid, fixedAnomalyProps(props, definition), scene, timezone, {
      tileGetterFactory: (emptyTile, failureTile) =>
        createBinaryAnomalyTileGetter(definition, emptyTile, failureTile, {
          onRequestStarted: () => {
            if (runtimeState.status !== "ready") {
              setRuntimeStatus(runtimeState, "loading", { model: props.model, stage: "request" });
            }
          },
          onRequestSucceeded: () => {
            setRuntimeStatus(runtimeState, "ready", { model: props.model, stage: "request" });
          },
          onRequestFailed: (error) => {
            setRuntimeStatus(
              runtimeState,
              isUnsupportedSourceError(error) ? "unsupported_source" : "source_error",
              { model: props.model, stage: "request" },
              error,
            );
          },
        }),
      fixedColormap: {
        id: definition.style.id,
        mapping: createBinaryAnomalyColorMapping(definition),
      },
      onUnsupportedHardware: (reason) => {
        setRuntimeStatus(runtimeState, "unsupported_hardware", { model: props.model, stage: "WebGL setup" }, reason);
      },
    });
    this.runtimeState = runtimeState;
    this.definition = definition;
  }

  humanReadableId(): string {
    return `metx.BIN.ANOMALY.${this.props.model}#${this.uid}`;
  }

  getActiveWeatherParametersAsString() {
    return [
      { model: this.props.model, parameter: this.definition.primaryParameter, ensSelect: "" },
      { model: this.props.model, parameter: this.definition.referenceParameter, ensSelect: "" },
    ];
  }

  getRuntimeStatus(): Readonly<MutableBinaryAnomalyRuntimeState> {
    return this.runtimeState;
  }
}

export function createBinaryAnomalyLayer(
  uid: number,
  baseLayer: WmsLayer,
  scene: ScenePublicApi,
  timezone: GuiTimeZone,
): BinaryAnomalyLayerImpl {
  const projection = scene.getMapboxMap().getProjection()?.name;
  if (projection !== ProjectionNameEnum.mercator) {
    throw new Error(`binary anomaly layer requires Mercator projection, got ${projection ?? "unknown"}`);
  }
  return new BinaryAnomalyLayerImpl(uid, baseLayer, scene, timezone);
}

export const createBinaryTemperatureAnomalyPocLayer = createBinaryAnomalyLayer;
